import { QueryClient } from "@tanstack/react-query"
import eq from "lodash-es/eq"
import isNumber from "lodash-es/isNumber"

import type { TTFunction } from "$/i18n/i18n"

import type { TCoachHeaderTab } from "@/3514/components"
import { ECoachTab, EPath, ERoute } from "@/3514/constants"
import { diagnosticSummaryQueryPrefetcher } from "@/3514/entities/diagnostic-summary"
import { coachGroupSessionSummaryConfigPrefetcher } from "@/3514/pages/coach.group"
import { coachingSessionsGroupQueryPrefetcher, coachingSessionsIndividualQueryPrefetcher } from "@/3514/queries"

function renderCoachTabs({
    groupId,
    participantId,
    lastModuleId,
    lastSessionId,
    tab,
    qc,
    t
}: {
    groupId: number
    participantId: number
    lastModuleId: number | null
    lastSessionId: number | null
    tab: string
    qc: QueryClient
    t: TTFunction
}): Record<ERoute.Coach_Group | ERoute.Coach_GroupParticipant | ERoute.Coach_IndividualParticipant, TCoachHeaderTab[]> {
    return {
        [ERoute.Coach_Group]: [
            {
                label: t("coachSide.header.meta.tabs.summary"),
                href: EPath.Coach_Group(
                    { groupId },
                    { tab: "Summary", ...(lastSessionId !== null && { sessionId: lastSessionId }) }
                ),
                isCurrent: eq(tab, ECoachTab.Group.Summary),
                async onHover(): Promise<void> {
                    await coachingSessionsGroupQueryPrefetcher({ qc }, { groupId })

                    if (isNumber(lastSessionId))
                        await coachGroupSessionSummaryConfigPrefetcher({ qc }, { sessionId: lastSessionId })
                }
            },
            {
                label: t("coachSide.header.meta.tabs.sessions"),
                href: EPath.Coach_Group({ groupId }, { tab: "Sessions" }),
                isCurrent: eq(tab, ECoachTab.Group.Sessions)
            }
        ],
        [ERoute.Coach_GroupParticipant]: [
            {
                label: t("coachSide.header.meta.tabs.modules"),
                href: EPath.Coach_GroupParticipant(
                    { groupId, participantId },
                    { tab: "Modules", ...(lastModuleId !== null && { moduleId: lastModuleId }) }
                ),
                isCurrent: eq(tab, ECoachTab.GroupParticipant.Modules)
            },
            {
                label: t("coachSide.header.meta.tabs.diagnostic"),
                href: EPath.Coach_GroupParticipant({ groupId, participantId }, { tab: "Diagnostic" }),
                isCurrent: eq(tab, ECoachTab.GroupParticipant.Diagnostic),
                async onHover(): Promise<void> {
                    await diagnosticSummaryQueryPrefetcher({ qc }, { participantId })
                }
            },
            {
                label: t("coachSide.header.meta.tabs.sessions"),
                href: EPath.Coach_GroupParticipant({ groupId, participantId }, { tab: "Sessions" }),
                isCurrent: eq(tab, ECoachTab.GroupParticipant.Sessions),
                async onHover(): Promise<void> {
                    await coachingSessionsIndividualQueryPrefetcher({ qc }, { participantId })
                }
            }
        ],
        [ERoute.Coach_IndividualParticipant]: [
            {
                label: t("coachSide.header.meta.tabs.diagnostic"),
                href: EPath.Coach_IndividualParticipant({ participantId }, { tab: "Diagnostic" }),
                isCurrent: eq(tab, ECoachTab.IndividualParticipant.Diagnostic),
                async onHover(): Promise<void> {
                    await diagnosticSummaryQueryPrefetcher({ qc }, { participantId })
                }
            },
            {
                label: t("coachSide.header.meta.tabs.sessions"),
                href: EPath.Coach_IndividualParticipant({ participantId }, { tab: "Sessions" }),
                isCurrent: eq(tab, ECoachTab.IndividualParticipant.Sessions),
                async onHover(): Promise<void> {
                    await coachingSessionsIndividualQueryPrefetcher({ qc }, { participantId })
                }
            },
            {
                label: t("coachSide.header.meta.tabs.plan"),
                href: EPath.Coach_IndividualParticipant({ participantId }, { tab: "Plan" }),
                isCurrent: eq(tab, ECoachTab.IndividualParticipant.Plan)
            }
        ]
    }
}

export default { renderCoachTabs }
